/**
 * Question flow for Local Bridge API: full list, progress bar, section sidebar, prev/next, jump by section.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, Radio, Button, Spin, Progress, message } from 'antd';
import { useParams } from 'react-router-dom';
import { useBridgeSession } from '../../contexts/BridgeSessionContext';
import {
  getQuestions,
  submitAnswers,
  calculateSession,
  type QuestionWithAnswer,
} from '../../services/localBridgeSessionApi';
import {
  getProductDomainQuestions,
  submitProductDomainAnswers,
  calculateProductDomainSession,
} from '../../services/productDomainSessionApi';
import styles from './QuestionFlow.module.css';

type Section = { domain: string; factor: string; indices: number[] };

function buildSections(questions: QuestionWithAnswer[]): Section[] {
  const byKey = new Map<string, number[]>();
  questions.forEach((q, i) => {
    const key = `${q.domain}\0${q.factor}`;
    if (!byKey.has(key)) byKey.set(key, []);
    byKey.get(key)!.push(i);
  });
  const seen = new Set<string>();
  const sections: Section[] = [];
  questions.forEach((q, i) => {
    const key = `${q.domain}\0${q.factor}`;
    if (seen.has(key)) return;
    seen.add(key);
    sections.push({ domain: q.domain, factor: q.factor, indices: byKey.get(key)! });
  });
  return sections;
}

export const QuestionFlow: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const { sessionType, navigateToStep } = useBridgeSession();
  const [questions, setQuestions] = useState<QuestionWithAnswer[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedScore, setSelectedScore] = useState<number | null>(null);
  const [answerDirty, setAnswerDirty] = useState(false);

  const loadQuestions = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    try {
      const list = sessionType === 'product'
        ? await getProductDomainQuestions(sessionId, 'current')
        : await getQuestions(sessionId, 'current');
      setQuestions(list as QuestionWithAnswer[]);
      setCurrentIndex((i) => (list.length ? Math.min(i, list.length - 1) : 0));
      setSelectedScore(null);
      setAnswerDirty(false);
    } catch {
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  }, [sessionId, sessionType]);

  useEffect(() => {
    loadQuestions();
  }, [loadQuestions]);

  const currentQuestion = questions.length > 0 ? questions[currentIndex] : null;
  const answeredCount = questions.filter((q) => q.current_answer != null).length;
  const totalCount = questions.length;
  const allAnswered = totalCount > 0 && answeredCount === totalCount;
  const progressPercent = totalCount ? Math.round((answeredCount / totalCount) * 100) : 0;

  // Sync selected score when changing question
  useEffect(() => {
    if (currentQuestion?.current_answer) {
      setSelectedScore(currentQuestion.current_answer.score);
      setAnswerDirty(false);
    } else {
      setSelectedScore(null);
      setAnswerDirty(false);
    }
  }, [currentIndex, currentQuestion?.question_id, currentQuestion?.current_answer]);

  const saveCurrentAnswer = useCallback(async () => {
    if (!sessionId || !currentQuestion || selectedScore == null) return false;
    setSubmitting(true);
    try {
      if (sessionType === 'product') {
        const pr = await submitProductDomainAnswers(sessionId, 'current', [
          { question_id: currentQuestion.question_id, score: selectedScore },
        ]);
        const ok = pr?.ok ?? false;
        if (ok) {
          setQuestions((prev) =>
            prev.map((q) =>
              q.question_id === currentQuestion.question_id
                ? { ...q, current_answer: { score: selectedScore } }
                : q
            )
          );
          setAnswerDirty(false);
          return true;
        }
        message.error('Failed to save answer');
        return false;
      }
      const result = await submitAnswers(sessionId, 'current', [
        { question_id: currentQuestion.question_id, score: selectedScore },
      ]);
      if (result.ok) {
        setQuestions((prev) =>
          prev.map((q) =>
            q.question_id === currentQuestion.question_id
              ? { ...q, current_answer: { score: selectedScore } }
              : q
          )
        );
        setAnswerDirty(false);
        return true;
      }
      message.error(result.error);
    } catch {
      message.error('Failed to save answer');
    } finally {
      setSubmitting(false);
    }
    return false;
  }, [sessionId, sessionType, currentQuestion, selectedScore]);

  const goTo = useCallback(
    async (nextIndex: number) => {
      if (nextIndex === currentIndex) return;
      if (answerDirty && currentQuestion && selectedScore != null) {
        const saved = await saveCurrentAnswer();
        if (!saved) return;
      }
      setCurrentIndex(nextIndex);
    },
    [currentIndex, answerDirty, currentQuestion, selectedScore, saveCurrentAnswer]
  );

  const handleNext = useCallback(async () => {
    if (!currentQuestion) return;
    if (selectedScore == null) {
      message.info('Please select an answer');
      return;
    }
    const saved = await saveCurrentAnswer();
    if (!saved) return;
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  }, [currentQuestion, selectedScore, saveCurrentAnswer, currentIndex, questions.length]);

  const handlePrev = useCallback(() => {
    if (currentIndex > 0) goTo(currentIndex - 1);
  }, [currentIndex, goTo]);

  const handleSidebarQuestionClick = useCallback(
    (index: number) => goTo(index),
    [goTo]
  );

  const handleCalculate = useCallback(async () => {
    if (!sessionId) return;
    setSubmitting(true);
    try {
      if (sessionType === 'product') {
        const result = await calculateProductDomainSession(sessionId);
        if (result != null) {
          message.success('Calculation complete');
          navigateToStep('entropy_heat_map');
        } else {
          message.error('Calculation failed');
        }
        return;
      }
      const calc = await calculateSession(sessionId);
      if (calc.ok) {
        message.success('Calculation complete');
        navigateToStep('entropy_heat_map');
      } else {
        message.error(calc.error);
      }
    } catch {
      message.error('Calculation failed');
    } finally {
      setSubmitting(false);
    }
  }, [sessionId, sessionType, navigateToStep]);

  if (loading && questions.length === 0) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <Card>
        <p>No questions available for this session.</p>
        <Button type="primary" onClick={loadQuestions}>
          Reload
        </Button>
      </Card>
    );
  }

  const sections = buildSections(questions);

  return (
    <div className={styles.wrapper}>
      <div className={styles.progressBarSection}>
        <div className={styles.label}>
          <span>Progress</span>
          <span>
            {answeredCount} of {totalCount} answered
          </span>
        </div>
        <div className={styles.bar}>
          <div
            className={styles.barFill}
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      <div className={styles.body}>
        <aside className={styles.sectionsSidebar}>
          {sections.map((sec) => (
            <div key={`${sec.domain}-${sec.factor}`} className={styles.sectionGroup}>
              <div className={styles.sectionTitle}>
                {sec.domain} / {sec.factor}
              </div>
              {sec.indices.map((idx) => {
                const q = questions[idx];
                const isCurrent = idx === currentIndex;
                const answered = q.current_answer != null;
                return (
                  <button
                    key={q.question_id}
                    type="button"
                    className={`${styles.questionItem} ${isCurrent ? styles.current : ''} ${answered ? styles.answered : ''}`}
                    onClick={() => handleSidebarQuestionClick(idx)}
                  >
                    <span className={styles.questionItemLabel}>
                      <span className={styles.dot} />
                      {q.topic || q.question_id}
                    </span>
                  </button>
                );
              })}
            </div>
          ))}
        </aside>

        <div className={styles.mainArea}>
          {currentQuestion && (
            <Card
              title={`${currentQuestion.domain} / ${currentQuestion.factor}`}
              className={styles.card}
            >
              <p style={{ marginBottom: 16, fontSize: 15 }}>{currentQuestion.text}</p>
              <Radio.Group
                value={selectedScore}
                onChange={(e) => {
                  setSelectedScore(e.target.value);
                  setAnswerDirty(true);
                }}
                style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
              >
                {currentQuestion.options.map((opt) => (
                  <Radio key={opt.score} value={opt.score}>
                    {opt.label}
                  </Radio>
                ))}
              </Radio.Group>
              <div className={styles.cardActions}>
                <Button
                  onClick={handlePrev}
                  disabled={currentIndex === 0}
                >
                  Previous
                </Button>
                <Button
                  type="primary"
                  onClick={handleNext}
                  disabled={selectedScore === null}
                  loading={submitting}
                >
                  Next
                </Button>
              </div>
              {allAnswered && (
                <div className={styles.calculateSection}>
                  <Button
                    type="primary"
                    size="large"
                    onClick={handleCalculate}
                    loading={submitting}
                  >
                    Calculate and continue to Entropy Heat Map
                  </Button>
                </div>
              )}
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuestionFlow;
